import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { couponInput, type Paginated } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { MaskedInput } from '@/components/shared/masked-input';
import { useTableParams } from '@/components/shared/use-table-params';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { centsToInput, money, parseMoney } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { formatInTimeZone } from 'date-fns-tz';
import { useServiceOptions, type Coupon } from './api';
import { applyServerError, fmtIsoDate, zodForm } from './lib';

interface Values {
  code: string;
  description: string;
  discountType: 'percent' | 'fixed';
  discountValue: string;
  maxRedemptions: string;
  maxPerCustomer: string;
  validFrom: string;
  validUntil: string;
  minAmount: string;
  serviceIds: string[];
  active: boolean;
}

const toPayload = (v: Values) => ({
  code: v.code,
  description: v.description,
  discountType: v.discountType,
  discountValue: v.discountType === 'percent' ? Number(v.discountValue) : parseMoney(v.discountValue),
  maxRedemptions: v.maxRedemptions.trim() === '' ? null : Number(v.maxRedemptions),
  maxPerCustomer: Number(v.maxPerCustomer),
  validFrom: v.validFrom,
  validUntil: v.validUntil,
  minAmountCents: parseMoney(v.minAmount),
  serviceIds: v.serviceIds,
  active: v.active,
});

const describe = (c: Coupon) => (c.discountType === 'percent' ? `${c.discountValue}%` : money(c.discountValue));

function CouponModal({ open, onOpenChange, coupon }: { open: boolean; onOpenChange: (v: boolean) => void; coupon: Coupon | null }) {
  const qc = useQueryClient();
  const tz = useTimezone();
  const services = useServiceOptions();
  const today = formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');
  const empty: Values = { code: '', description: '', discountType: 'percent', discountValue: '10', maxRedemptions: '', maxPerCustomer: '1', validFrom: today, validUntil: '', minAmount: '0,00', serviceIds: [], active: true };
  const form = useForm<Values>({ resolver: zodForm<Values>(couponInput, toPayload, { minAmountCents: 'minAmount', discountValue: 'discountValue' }), defaultValues: empty });
  const { errors } = form.formState;
  const type = form.watch('discountType');
  useEffect(() => {
    if (!open) return;
    form.reset(
      coupon
        ? {
            code: coupon.code,
            description: coupon.description ?? '',
            discountType: coupon.discountType,
            discountValue: coupon.discountType === 'percent' ? String(coupon.discountValue) : centsToInput(coupon.discountValue),
            maxRedemptions: coupon.maxRedemptions != null ? String(coupon.maxRedemptions) : '',
            maxPerCustomer: String(coupon.maxPerCustomer),
            validFrom: coupon.validFrom,
            validUntil: coupon.validUntil,
            minAmount: centsToInput(coupon.minAmountCents),
            serviceIds: coupon.serviceIds,
            active: coupon.active,
          }
        : empty,
    );
  }, [open, coupon]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: Values) => {
      const body = couponInput.parse(toPayload(v));
      return coupon ? api.put(`/coupons/${coupon.id}`, body) : api.post('/coupons', body);
    },
    onSuccess: () => {
      toast.success('Cupom salvo');
      qc.invalidateQueries({ queryKey: ['loyalty', 'coupons'] });
      onOpenChange(false);
    },
    onError: (e) => applyServerError(form, e, { minAmountCents: 'minAmount' }),
  });

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={coupon ? 'Editar cupom' : 'Novo cupom'}
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button type="submit" form="coupon-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar cupom'}
          </Button>
        </>
      }
    >
      <form id="coupon-form" className="grid gap-4" noValidate onSubmit={form.handleSubmit((v) => save.mutate(v))}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="cp-code" label="Código" hint="Letras, números, hífen e sublinhado." error={errors.code?.message}>
            <Input id="cp-code" autoFocus className="uppercase" {...form.register('code')} />
          </Field>
          <Field id="cp-desc" label="Descrição (opcional)" error={errors.description?.message}>
            <Input id="cp-desc" maxLength={200} {...form.register('description')} />
          </Field>
          <Field id="cp-type" label="Tipo de desconto">
            <Controller
              control={form.control}
              name="discountType"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(v) => {
                    field.onChange(v);
                    form.setValue('discountValue', v === 'percent' ? '10' : '10,00');
                  }}
                >
                  <SelectTrigger id="cp-type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentual</SelectItem>
                    <SelectItem value="fixed">Valor fixo</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field id="cp-value" label={type === 'percent' ? 'Desconto (%)' : 'Desconto (R$)'} error={errors.discountValue?.message}>
            {type === 'percent' ? <Input id="cp-value" inputMode="numeric" {...form.register('discountValue')} /> : <MaskedInput mask="money" id="cp-value" {...form.register('discountValue')} />}
          </Field>
          <Field id="cp-from" label="Válido a partir de" error={errors.validFrom?.message}>
            <Input id="cp-from" type="date" {...form.register('validFrom')} />
          </Field>
          <Field id="cp-until" label="Válido até" error={errors.validUntil?.message}>
            <Input id="cp-until" type="date" {...form.register('validUntil')} />
          </Field>
          <Field id="cp-min" label="Valor mínimo do atendimento" error={errors.minAmount?.message}>
            <MaskedInput mask="money" id="cp-min" {...form.register('minAmount')} />
          </Field>
          <Field id="cp-max" label="Limite total de usos" hint="Em branco = sem limite." error={errors.maxRedemptions?.message}>
            <Input id="cp-max" inputMode="numeric" {...form.register('maxRedemptions')} />
          </Field>
          <Field id="cp-per" label="Usos por cliente" error={errors.maxPerCustomer?.message}>
            <Input id="cp-per" inputMode="numeric" {...form.register('maxPerCustomer')} />
          </Field>
        </div>
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-medium">Vale para quais serviços</legend>
          <p className="text-xs text-muted-foreground">Sem nenhum marcado, vale para todos os serviços.</p>
          <Controller
            control={form.control}
            name="serviceIds"
            render={({ field }) => (
              <div className="grid max-h-36 gap-2 overflow-y-auto rounded-lg border p-3">
                {services.data?.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={field.value.includes(s.id)} onCheckedChange={(c) => field.onChange(c ? [...field.value, s.id] : field.value.filter((x) => x !== s.id))} />
                    {s.name}
                  </label>
                ))}
              </div>
            )}
          />
        </fieldset>
        <div className="flex items-center gap-3">
          <Controller control={form.control} name="active" render={({ field }) => <Switch id="cp-active" checked={field.value} onCheckedChange={field.onChange} />} />
          <label htmlFor="cp-active" className="text-sm">
            Cupom ativo
          </label>
        </div>
      </form>
    </FormModal>
  );
}

export function CouponsTab() {
  const qc = useQueryClient();
  const tz = useTimezone();
  const today = formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');
  const params = useTableParams({ sort: 'createdAt', order: 'desc' });
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [open, setOpen] = useState(false);
  const list = useQuery({
    queryKey: ['loyalty', 'coupons', params.query],
    queryFn: () => api.get<Paginated<Coupon>>('/coupons', params.query),
    placeholderData: (p) => p,
  });
  const remove = async (c: Coupon) => {
    try {
      await api.del(`/coupons/${c.id}`);
      toast.success('Cupom excluído');
      qc.invalidateQueries({ queryKey: ['loyalty', 'coupons'] });
    } catch (e) {
      toast.error(errorMessage(e));
      throw e;
    }
  };
  const columns: DataColumn<Coupon>[] = [
    {
      id: 'code',
      header: 'Cupom',
      meta: { sortKey: 'code' },
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="font-medium tracking-wide">{row.original.code}</p>
          {row.original.description && <p className="max-w-60 truncate text-xs text-muted-foreground">{row.original.description}</p>}
        </div>
      ),
    },
    { id: 'discount', header: 'Desconto', cell: ({ row }) => <span className="tabular">{describe(row.original)}</span> },
    {
      id: 'validUntil',
      header: 'Vigência',
      meta: { sortKey: 'validUntil' },
      cell: ({ row }) => <span className="tabular">{fmtIsoDate(row.original.validFrom)} a {fmtIsoDate(row.original.validUntil)}</span>,
    },
    {
      id: 'redemptionsCount',
      header: 'Usos',
      meta: { sortKey: 'redemptionsCount' },
      cell: ({ row }) => <span className="tabular">{row.original.redemptionsCount}{row.original.maxRedemptions != null ? ` de ${row.original.maxRedemptions}` : ''}</span>,
    },
    {
      id: 'status',
      header: 'Situação',
      cell: ({ row }) => {
        const c = row.original;
        const expired = c.validUntil < today;
        const exhausted = c.maxRedemptions != null && c.redemptionsCount >= c.maxRedemptions;
        const label = !c.active ? 'Inativo' : expired ? 'Expirado' : exhausted ? 'Esgotado' : 'Ativo';
        return <Badge variant={label === 'Ativo' ? 'secondary' : 'outline'}>{label}</Badge>;
      },
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      meta: { align: 'right' },
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" aria-label={`Editar ${row.original.code}`} onClick={() => { setEditing(row.original); setOpen(true); }}>
            <Pencil aria-hidden />
          </Button>
          <ConfirmDialog
            trigger={
              <Button variant="ghost" size="icon" aria-label={`Excluir ${row.original.code}`}>
                <Trash2 className="text-destructive" aria-hidden />
              </Button>
            }
            title={`Excluir o cupom ${row.original.code}?`}
            description="Ele deixa de funcionar no caixa. O histórico de usos é mantido."
            confirmLabel="Excluir cupom"
            onConfirm={() => remove(row.original)}
          />
        </div>
      ),
    },
  ];
  const create = (
    <Button onClick={() => { setEditing(null); setOpen(true); }}>
      <Plus aria-hidden /> Novo cupom
    </Button>
  );
  return (
    <>
      <DataTable
        columns={columns}
        data={list.data}
        params={params}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={() => list.refetch()}
        searchPlaceholder="Buscar cupom"
        toolbar={create}
        empty={{ title: 'Nenhum cupom', description: 'Crie um código de desconto para usar no caixa ou em campanhas.', action: create }}
      />
      <CouponModal open={open} onOpenChange={setOpen} coupon={editing} />
    </>
  );
}
